import { AfterContentChecked, Component, Inject, OnDestroy, OnInit, Optional, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SignUpStatus } from 'src/app/services/auth/logic/auth-logic.service.interface';
import { Platform } from 'src/config/config.model';
import { AuthProvider } from 'src/app/services/yeti-protocol/auth/provider';
import { DeepLinksStorageService } from 'src/app/services/deep-links/deep-links-storage.service';
import { NavControllerService } from 'src/app/services/nav-controller.service';

// import Swiper core and required modules
import SwiperCore, { Autoplay, Pagination, SwiperOptions } from 'swiper';
import { SwiperComponent } from 'swiper/angular';

// services
import { AuthService } from 'src/app/services/auth/auth.service';
import { TRACKING_SERVICE, TrackingService } from 'src/app/services/tracking/tracking.model';
import {
  ActionSource,
  ActionTracked,
  SignInSignUpTrackingParam,
  TrackingRequest
} from 'src/app/services/yeti-protocol/tracking';
import { AUTH_UI_SERVICE, AuthDialogPage, AuthUIServiceInterface } from 'src/app/services/auth/auth-ui.service.interface';

import appConfig from 'src/config/config';
import { isMobilePlatform } from 'src/app/services/utils/utils';
import { SafariViewController } from '@awesome-cordova-plugins/safari-view-controller/ngx';
import { Subscription } from 'rxjs';
import { CONTEXT_SERVICE, ContextService } from 'src/app/services/context/context.model';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
// install Swiper modules
SwiperCore.use([Autoplay, Pagination]);

interface AuthPageConfig {
  clientId: string;
  platform: Platform
}

@Component({
  selector: 'app-auth',
  templateUrl: './auth.page.html',
  styleUrls: ['./auth.page.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AuthPage implements AfterContentChecked, OnInit, OnDestroy {
  @ViewChild('swiper') swiper: SwiperComponent;

  configSwiper: SwiperOptions = {
    autoplay: {
      delay: 5000,
      disableOnInteraction: false,
    },
    pagination: true
  };

  hasAO = false;
  dialogOpened = false;
  AuthProvider = AuthProvider;
  preventDeepLinkCheck = false;
  disableButtonsAndShowSpinner = false;

  config: AuthPageConfig = {
    clientId: appConfig.authentication.clientId,
    platform: appConfig.platform
  };
  ActionSource = ActionSource;

  private routeQueryParamsSubscription: Subscription;

  constructor(
    private route: ActivatedRoute,
    private authService: AuthService,
    @Inject(TRACKING_SERVICE) private trackingService: TrackingService,
    private navController: NavControllerService,
    private deepLinksStorage: DeepLinksStorageService,
    @Inject(AUTH_UI_SERVICE) private authUIService: AuthUIServiceInterface,
    private router: Router,
    @Optional() private safariViewController: SafariViewController,
    @Inject(CONTEXT_SERVICE) private contextService: ContextService,
    @Optional() private gtmService: GoogleTagManagerService,
  ) {
    this.preventDeepLinkCheck = this.router.getCurrentNavigation()?.extras?.state?.preventDeepLinkCheck;
  }

  ngOnInit(): void {

    this._hideSafaryView();

    this.routeQueryParamsSubscription = this.route.queryParams.subscribe(params => {

      if (params.state === 'ios') {
        this._hideSafaryView();
      }
    });
  }

  ionViewWillEnter(): void {
    this._hideSafaryView();
  }

  ngAfterContentChecked(): void {
    if (this.swiper && this.swiper.swiperRef) {
      this.swiper.swiperRef.params.autoplay = {
        delay: 5000,
        disableOnInteraction: false,
      };
      this.swiper.swiperRef.autoplay.start();
      this.swiper.updateSwiper({});
    }
  }

  ngOnDestroy(): void {
    this.routeQueryParamsSubscription?.unsubscribe();
  }

  get isMobileDevice(): boolean {
    return isMobilePlatform(this.config.platform);
  }

  signIn(provider: AuthProvider): void {
    if (provider === AuthProvider.MI) {
      this.dialogOpened = true;
      this.authUIService.openDialog(AuthDialogPage.SignIn);
      return;
    }
    this.trackSignUpStartAction(provider);
    this.authUIService.signIn(provider);
  }

  async ionViewDidEnter(): Promise<any> {

    if (appConfig?.googleAnalyticsId && this.gtmService) {

      const gtmTag = {
        event: 'page',
        pageName: window.location.href
      };

      this.gtmService.pushTag(gtmTag);
    }

    this.disableAuthButtons();
    const page = this.route.snapshot.data.page;
    const showAccessBlockedMessage = this.route.snapshot.data.showAccessBlockedMessage;

    if (showAccessBlockedMessage) {
      try {
        await this.authUIService.showAccessBlockedDialog();
      } catch (err) {
        console.error(err);
      } finally {
        this.enableAuthButtons();
      }
    }

    try {

      let userContext = '';

      try {
        userContext = (await this.contextService.getCurrentContext())?.key;
      } catch (err) {
        userContext = this.config.clientId;
      }

      const status = await this.authService.getSignupStatus(userContext || this.config.clientId);
      this.trackAuthPageOpened(); // before redirect track page loaded for analytic purposes
      if (status === SignUpStatus.FINISHED) {

        const deepLink = await this.deepLinksStorage.getSavedDeepLink();
        this.preventDeepLinkCheck = window.history?.state?.preventDeepLinkCheck;

        let deepLinkWithoutQueryParams = deepLink;

        if (deepLinkWithoutQueryParams?.length && deepLinkWithoutQueryParams?.includes('?')) {
          deepLinkWithoutQueryParams = deepLinkWithoutQueryParams.split('?')?.[0];
        }

        if (!this.preventDeepLinkCheck &&
          deepLinkWithoutQueryParams &&
          deepLinkWithoutQueryParams !== '/auth' &&
          deepLinkWithoutQueryParams !== '/ao-auth-signup') {

          const navigateRootPromise = this.navController.navigateRoot(deepLink);
          navigateRootPromise?.finally(() => this.enableAuthButtons());
          return navigateRootPromise;
        }

        const navigateHomePromise = this.authUIService.navigateToHome(null);
        navigateHomePromise?.finally(() => this.enableAuthButtons());
        return navigateHomePromise;
      }
      if (page) {
        const openDialogPromise = this.authUIService.openDialog(page);
        openDialogPromise?.finally(() => this.enableAuthButtons());
        return openDialogPromise;
      }
      return this.authUIService.finishSignUpIfRequired()
        .then(opened => {
          if (!opened) {
            if (this.isMobileDevice) {
              const openDialogPromise = this.authUIService.openDialog(AuthDialogPage.Welcome);
              openDialogPromise?.finally(() => this.enableAuthButtons());
              return openDialogPromise;
            }
          }
        }).finally(() => {
          this.enableAuthButtons();
        });
    } catch (err) {
      console.error(err);
    } finally {
      this.enableAuthButtons();
    }
  }

  private disableAuthButtons(): void {
    this.disableButtonsAndShowSpinner = true;
  }

  private enableAuthButtons(): void {
    setTimeout(() => {
      this.disableButtonsAndShowSpinner = false;
    }, 1000); // delay of 1 second to be sure that the navigation is finished
  }

  onOpenAbout(): void {
    this.navController.navigateForward('/about');
  }

  private trackSignUpStartAction(provider): void {
    const paramsToTrack: SignInSignUpTrackingParam = {
      provider: provider,
      source: ActionSource.myAoLoginPage
    };

    const trackData: TrackingRequest = {
      action: ActionTracked.signUpStart,
      params: paramsToTrack
    };
    this.trackingService.track(trackData).catch(_err => {
      console.error('Could not track signup-start action');
    });
  }

  private trackAuthPageOpened(): void {
    this.trackingService.trackGenericPageOpenedAction('authentication', ActionSource.authenticationPage)
      .catch(_err => {
        console.error('Could not track authentication page loaded action.');
      });
  }

  _hideSafaryView(): Promise<void> {
    if (this.safariViewController) {
      return this.safariViewController.hide();
    }
    return Promise.resolve();
  }
}
